import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tags } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { api, companyQuery } from "@web/lib/queries";
import { allVendorCategoriesQuery, vendorCategoryApi, vendorsQuery, type VendorCategory } from "@web/lib/queries-vendors";
import { useMe } from "@web/lib/session";
import {
  BottomSheet, Button, Card, EmptyState, Inline, Page, Region, Row, RowGroup, Skeleton, Stack,
  StatusChip, Text, TextField, Toggle, useToast, Workspace,
} from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › Vendor categories (SPEC §10.5, §3.11).
 *
 * The taxonomy the directory is organised by. It arrives seeded and opinionated,
 * which is the point: a venue should not have to invent "Draping & lighting"
 * before it can file its first vendor. Renaming is the common case, adding is
 * occasional, and switching one off hides it from new vendors without touching
 * the ones already filed under it.
 *
 * Deactivated categories are shown, greyed, rather than hidden — otherwise a
 * vendor sitting in one becomes impossible to explain.
 */
export function SettingsVendorCategories() {
  const me = useMe();
  const toast = useToast();
  const all = useQuery({ ...allVendorCategoriesQuery, enabled: me.companyAdmin });
  const vendors = useQuery({ ...vendorsQuery({ limit: 200 }), enabled: me.companyAdmin });
  const company = useQuery({ ...companyQuery, enabled: me.companyAdmin });
  const [editing, setEditing] = useState<VendorCategory | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  if (!me.companyAdmin) return <AdminsOnly />;

  const counts = new Map<string, number>();
  for (const vendor of vendors.data ?? []) {
    counts.set(vendor.categoryId ?? "", (counts.get(vendor.categoryId ?? "") ?? 0) + 1);
  }

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    try { await action(); toast.success(done); setEditing(null); setAdding(false); setName(""); }
    catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  return <Page>
    <Workspace variant="form"><Region area="main"><Stack gap={4}>
      <Card>
        <Stack gap={2}>
          <Text as="h2" variant="title">What your vendors are filed under</Text>
          <Text variant="caption" tone="secondary">
            Rename anything that does not match how your team talks. Switching a category off hides it when filing a new vendor;
            the vendors already in it keep it.
          </Text>
          <Button fullWidth={false} onClick={() => { setName(""); setAdding(true); }}>Add a category</Button>
        </Stack>
      </Card>

      <Card>
        <Stack gap={2}>
          <Inline justify="between" align="center" wrap>
            <Stack gap={1}>
              <Text as="h2" variant="title">Show preferred vendors to clients</Text>
              <Text variant="caption" tone="secondary">
                Adds "Recommended vendors for {"{venue}"}" to the quote and the client's booking page: name, category and a way to reach them.
                Only vendors you have marked preferred at that venue, and only their public details.
              </Text>
            </Stack>
            <Toggle
              label="Show preferred vendors to clients"
              checked={company.data?.settings.showPreferredVendorsToClients === 1}
              disabled={busy || !company.data}
              onChange={next => void run(
                () => api.patchCompany({ settings: { showPreferredVendorsToClients: next ? 1 : 0 } }),
                next ? "Clients will see your preferred vendors" : "Clients will not see your vendors")}
            />
          </Inline>
          <Text variant="caption" tone="tertiary">
            Off by default. Recommending a business to a client is your endorsement to make.
          </Text>
        </Stack>
      </Card>

      {all.isError
        ? <EmptyState icon={Tags} title="Categories could not be loaded" body={errorMessage(all.error)}
            action={<Button onClick={() => void all.refetch()}>Try again</Button>} />
        : !all.data ? <Skeleton count={6} />
          : all.data.length === 0
            ? <EmptyState icon={Tags} title="No categories" body="Add the first one, or re-run the seed to get the standard list." />
            : <Card>
              <RowGroup>
                {all.data.map(category => (
                  <Row key={category.id} wrap
                    title={category.name}
                    subtitle={`${counts.get(category.id) ?? 0} vendor${(counts.get(category.id) ?? 0) === 1 ? "" : "s"}`}
                    trailing={category.active === 0 ? <StatusChip tone="neutral">Off</StatusChip> : undefined}
                    onClick={() => { setName(category.name); setEditing(category); }} />
                ))}
              </RowGroup>
            </Card>}
    </Stack></Region></Workspace>

    <BottomSheet open={adding} onClose={() => setAdding(false)} title="Add a category"
      footer={<Button loading={busy} disabled={!name.trim()} onClick={() => void run(() => vendorCategoryApi.create({ name: name.trim() }), "Category added")}>Add it</Button>}>
      <Stack gap={3}>
        <TextField label="Name" value={name} onChange={event => setName(event.target.value)} autoFocus maxLength={80} />
        <Text variant="caption" tone="tertiary">It appears in the directory and in the picker on a booking.</Text>
      </Stack>
    </BottomSheet>

    {editing && (
      <BottomSheet open onClose={() => setEditing(null)} title={editing.name}
        footer={<Button loading={busy} disabled={!name.trim() || name.trim() === editing.name}
          onClick={() => void run(() => vendorCategoryApi.update(editing.id, { name: name.trim() }), "Category renamed")}>Save the name</Button>}>
        <Stack gap={3}>
          <TextField label="Name" value={name} onChange={event => setName(event.target.value)} autoFocus maxLength={80} />
          <Inline justify="between" align="center">
            <Stack gap={1}>
              <Text>Offer this category</Text>
              <Text variant="caption" tone="secondary">
                {editing.key === "other"
                  ? "Other is where a vendor goes when nothing else fits, so it stays on."
                  : "Off hides it when filing a new vendor. Vendors already in it keep it."}
              </Text>
            </Stack>
            <Toggle
              label={`Offer ${editing.name}`}
              checked={editing.active === 1}
              disabled={busy || editing.key === "other"}
              onChange={next => void run(() => vendorCategoryApi.update(editing.id, { active: next ? 1 : 0 }), next ? "Category is on" : "Category is off")}
            />
          </Inline>
          <Text variant="caption" tone="tertiary">
            {counts.get(editing.id) ?? 0} vendor{(counts.get(editing.id) ?? 0) === 1 ? "" : "s"} filed here.
          </Text>
        </Stack>
      </BottomSheet>
    )}
  </Page>;
}
